import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "../Notification/NotificationManagementPage.css";
import "../Trading/AddCurrencyPairPage.css";
import "./SendEmailPage.css";

const isValidEmail = (str) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

const TABS = [
  { key: "send", label: "Send Email", icon: "fa-paper-plane" },
  { key: "sent", label: "Sent Emails", icon: "fa-history" },
];

const SendEmailPage = () => {
  const [activeTab, setActiveTab] = useState("send");
  const [form, setForm] = useState({ toEmail: "", subject: "", message: "" });
  const [sentEmails, setSentEmails] = useState([]);
  const [successModal, setSuccessModal] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestionIndex, setSuggestionIndex] = useState(-1);
  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userSearchError, setUserSearchError] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendError, setSendError] = useState("");
  const [sentLoading, setSentLoading] = useState(false);
  const [sentError, setSentError] = useState("");
  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const wrapperRef = useRef(null);
  const emailSearchRequestRef = useRef(0);
  const attachmentInputRef = useRef(null);

  const searchTerm = form.toEmail.trim().toLowerCase();
  const suggestions = useMemo(
    () =>
      emailSuggestions.map((u) => ({
        id: u._id || u.id,
        name: u.firstName || u.emailId || "User",
        email: u.emailId,
      })),
    [emailSuggestions]
  );

  useEffect(() => {
    const q = form.toEmail.trim();
    if (!q) {
      setEmailSuggestions([]);
      setUserSearchLoading(false);
      setUserSearchError("");
      return;
    }
    const handle = setTimeout(() => {
      const requestId = Date.now();
      emailSearchRequestRef.current = requestId;
      setUserSearchLoading(true);
      setUserSearchError("");
      AdminService.findUserByEmailForSendEmail(q)
        .then((res) => {
          if (emailSearchRequestRef.current !== requestId) return;
          if (res?.success) {
            setEmailSuggestions(Array.isArray(res.data) ? res.data : []);
          } else {
            setEmailSuggestions([]);
            setUserSearchError(res?.message || "Unable to search users.");
          }
        })
        .catch((err) => {
          if (emailSearchRequestRef.current !== requestId) return;
          setEmailSuggestions([]);
          setUserSearchError(err?.response?.data?.message || err?.message || "Unable to search users.");
        })
        .finally(() => {
          if (emailSearchRequestRef.current === requestId) {
            setUserSearchLoading(false);
          }
        });
    }, 350);
    return () => clearTimeout(handle);
  }, [form.toEmail]);

  useEffect(() => {
    setSuggestionIndex(-1);
  }, [emailSuggestions]);

  const normalizeSentEmailRows = useCallback((items = []) => {
    return (Array.isArray(items) ? items : []).map((row) => {
      const firstName = row?.userId?.firstName || "";
      const lastName = row?.userId?.lastName || "";
      const fullName = `${firstName} ${lastName}`.trim();
      return {
        _id: row?._id || `em_${Date.now()}`,
        to: row?.recipientEmail || row?.userId?.emailId || "—",
        toName: fullName || row?.recipientEmail || "User",
        subject: row?.subject || "—",
        sentAt: row?.createdAt || row?.updatedAt,
        attachmentsCount: Array.isArray(row?.attachments) ? row.attachments.length : 0,
      };
    });
  }, []);

  const fetchSentEmails = useCallback(async () => {
    setSentLoading(true);
    setSentError("");
    try {
      const res = await AdminService.getAdminEmailList();
      if (res?.success) {
        setSentEmails(normalizeSentEmailRows(res?.data));
      } else {
        setSentError(res?.message || "Failed to fetch sent emails.");
      }
    } catch (err) {
      setSentError(err?.response?.data?.message || err?.message || "Failed to fetch sent emails.");
    } finally {
      setSentLoading(false);
    }
  }, [normalizeSentEmailRows]);

  useEffect(() => {
    if (activeTab === "sent") {
      fetchSentEmails();
    }
  }, [activeTab, fetchSentEmails]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const updateForm = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (key === "toEmail" || key === "subject" || key === "message") {
      setSendError("");
    }
    if (key === "toEmail") {
      setShowSuggestions(true);
      setSuggestionIndex(-1);
    }
  };

  const selectSuggestion = (user) => {
    updateForm("toEmail", user.email);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSuggestionIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSuggestionIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1));
    } else if (e.key === "Enter" && suggestionIndex >= 0 && suggestions[suggestionIndex]) {
      e.preventDefault();
      selectSuggestion(suggestions[suggestionIndex]);
    } else if (e.key === "Escape") {
      setShowSuggestions(false);
      setSuggestionIndex(-1);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const email = form.toEmail.trim();
    if (!email || !form.subject || !form.message) return;
    if (!isValidEmail(email)) return;
    setSendError("");
    const match = emailSuggestions.find(
      (u) => (u.emailId || "").toLowerCase() === email.toLowerCase()
    );
    setSendingEmail(true);
    try {
      const formData = new FormData();
      formData.append("emailId", email);
      formData.append("subject", form.subject.trim());
      formData.append("message", form.message.trim());
      attachmentFiles.forEach((file) => {
        formData.append("attachments", file);
      });
      const res = await AdminService.sendEmailToUser(formData);
      if (res?.success) {
        const recipientEmail = res?.data?.recipientEmail || email;
        const recipientName = res?.data?.recipientName || match?.firstName || recipientEmail.split("@")[0];
        setSentEmails((prev) => [
          {
            _id: `em_${Date.now()}`,
            to: recipientEmail,
            toName: recipientName,
            subject: form.subject.trim(),
            sentAt: new Date().toISOString(),
          },
          ...prev,
        ]);
        setSuccessModal({
          to: recipientEmail,
          subject: form.subject.trim(),
          isRegistered: !!match,
        });
        if (activeTab === "sent") fetchSentEmails();
        setForm({ toEmail: "", subject: "", message: "" });
        setAttachmentFiles([]);
        if (attachmentInputRef.current) attachmentInputRef.current.value = "";
        setShowSuggestions(false);
      } else {
        setSendError(res?.message || "Failed to send email.");
      }
    } catch (err) {
      setSendError(err?.response?.data?.message || err?.message || "Failed to send email.");
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <main className="dashboard-main dashboard-subpage notification-management-page">
      <section className="dashboard-hero notification-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Send Email</span>
          </p>
          <h1 className="dashboard-hero__title">Send Email to User</h1>
          <p className="dashboard-hero__subtitle">
            Send to registered users or any email address
          </p>
        </div>
        <div className="notification-hero__icon">
          <i className="fas fa-envelope" />
        </div>
      </section>

      <div className="dashboard-body">
        {sentEmails.length > 0 && (
          <div className="notification-stats send-email-stats">
            <div className="notification-stat-card">
              <span className="notification-stat-card__value">{sentEmails.length}</span>
              <span className="notification-stat-card__label">Total Sent Emails</span>
            </div>
          </div>
        )}

        <div className="notification-tabs">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`notification-tab ${activeTab === tab.key ? "notification-tab--active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              <i className={`fas ${tab.icon}`} /> {tab.label}
            </button>
          ))}
        </div>

        <div className="notification-content">
          {activeTab === "send" && (
            <section className="notification-section">
              <h2 className="notification-section__title">
                <i className="fas fa-paper-plane" />
                Compose Email
              </h2>
              <form className="notification-form" onSubmit={handleSubmit}>
                <div className="notification-form__group notification-form__group--relative send-email-to-wrap" ref={wrapperRef}>
                  <label>To (Email) <span className="required">*</span></label>
                  <input
                    type="text"
                    placeholder="Type email to search registered users..."
                    value={form.toEmail}
                    onChange={(e) => updateForm("toEmail", e.target.value)}
                    onFocus={() => setShowSuggestions(true)}
                    onKeyDown={handleKeyDown}
                    autoComplete="off"
                  />
                  {showSuggestions && (
                    <div className="notification-suggestions send-email-suggestions">
                      {!form.toEmail.trim() ? (
                        <div className="send-email-suggest-header">Type an email to search registered users</div>
                      ) : userSearchLoading ? (
                        <div className="send-email-suggest-empty">Searching users…</div>
                      ) : userSearchError ? (
                        <div className="send-email-suggest-empty send-email-suggest-error">{userSearchError}</div>
                      ) : suggestions.length > 0 ? (
                        <>
                          <div className="send-email-suggest-header">Matching users</div>
                          {suggestions.map((u, idx) => (
                            <div
                              key={u.id}
                              role="button"
                              tabIndex={0}
                              className={`notification-suggestion-item ${idx === suggestionIndex ? "send-email-suggest-active" : ""}`}
                              onClick={() => selectSuggestion(u)}
                              onKeyDown={(e) => e.key === "Enter" && selectSuggestion(u)}
                            >
                              <div className="notification-suggestion-name">{u.name}</div>
                              <div className="notification-suggestion-email">{u.email}</div>
                            </div>
                          ))}
                          {searchTerm &&
                            suggestions.every((u) => u.email.toLowerCase() !== searchTerm) &&
                            isValidEmail(form.toEmail.trim()) && (
                              <div className="send-email-suggest-custom">
                                Or send to: <strong>{form.toEmail.trim()}</strong> (any email)
                              </div>
                            )}
                        </>
                      ) : (
                        <div className="send-email-suggest-empty">
                          No registered user match. You can still send to any valid email above.
                        </div>
                      )}
                    </div>
                  )}
                  {/* {form.toEmail && !isValidEmail(form.toEmail.trim()) && (
                    <small className="send-email-error">Please enter a valid email address</small>
                  )} */}
                </div>
                <div className="notification-form__group">
                  <label>Subject <span className="required">*</span></label>
                  <input
                    type="text"
                    placeholder="Email subject"
                    value={form.subject}
                    onChange={(e) => updateForm("subject", e.target.value)}
                  />
                </div>
                <div className="notification-form__group">
                  <label>Message <span className="required">*</span></label>
                  <textarea
                    placeholder="Write your message here..."
                    value={form.message}
                    onChange={(e) => updateForm("message", e.target.value)}
                    rows={5}
                  />
                </div>
                <div className="notification-form__group">
                  <label htmlFor="send-email-attachments">Attachments (optional)</label>
                  <input
                    id="send-email-attachments"
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    className="send-email-attachments-input"
                    onChange={(e) => {
                      const list = e.target.files ? Array.from(e.target.files) : [];
                      setAttachmentFiles((prev) => [...prev, ...list]);
                      setSendError("");
                      e.target.value = "";
                    }}
                  />
                  <p className="send-email-attachments-hint">You can add multiple files. They are sent as <code>attachments</code> in the form.</p>
                  {attachmentFiles.length > 0 && (
                    <ul className="send-email-attachments-list">
                      {attachmentFiles.map((file, idx) => (
                        <li key={`${file.name}-${idx}-${file.size}`} className="send-email-attachments-item">
                          <span className="send-email-attachments-name">{file.name}</span>
                          <span className="send-email-attachments-size">{(file.size / 1024).toFixed(1)} KB</span>
                          <button
                            type="button"
                            className="send-email-attachments-remove"
                            onClick={() => setAttachmentFiles((prev) => prev.filter((_, i) => i !== idx))}
                            aria-label={`Remove ${file.name}`}
                          >
                            ×
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="notification-form__actions send-email-actions">
                  <button
                    type="button"
                    className="send-email-btn-clear"
                    onClick={() => {
                      setForm({ toEmail: "", subject: "", message: "" });
                      setAttachmentFiles([]);
                      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
                    }}
                    disabled={sendingEmail}
                  >
                    Clear
                  </button>
                  <button
                    type="submit"
                    className="notification-btn-send"
                    disabled={sendingEmail || !form.toEmail.trim() || !isValidEmail(form.toEmail.trim()) || !form.subject || !form.message}
                  >
                    <i className="fas fa-paper-plane" /> {sendingEmail ? "Sending..." : "Send Email"}
                  </button>
                </div>
                {!!sendError && <small className="send-email-error">{sendError}</small>}
              </form>
            </section>
          )}

          {activeTab === "sent" && (
            <section className="notification-section">
              <h2 className="notification-section__title">
                <i className="fas fa-list-alt" />
                Sent Emails List ({sentEmails.length})
              </h2>
              {sentLoading ? (
                <div className="notification-empty">
                  <i className="fas fa-spinner fa-spin" />
                  <p>Loading sent emails...</p>
                </div>
              ) : sentError ? (
                <div className="notification-empty">
                  <i className="fas fa-exclamation-circle" />
                  <p>{sentError}</p>
                </div>
              ) : sentEmails.length === 0 ? (
                <div className="notification-empty">
                  <i className="fas fa-envelope" />
                  <p>No emails sent yet</p>
                </div>
              ) : (
                <div className="notification-table-wrap">
                  <table className="notification-table">
                    <thead>
                      <tr>
                        <th>Sent At</th>
                        <th>To</th>
                        <th>Subject</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sentEmails.map((row) => (
                        <tr key={row._id}>
                          <td>{formatDate(row.sentAt)}</td>
                          <td>
                            <div><strong>{row.toName}</strong></div>
                            <div className="notification-suggestion-email">{row.to}</div>
                          </td>
                          <td>
                            <div>{row.subject}</div>
                            {row.attachmentsCount > 0 && (
                              <div className="notification-suggestion-email">
                                Attachments: {row.attachmentsCount}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {successModal && (
        <div className="addpair-success-overlay" onClick={() => setSuccessModal(null)}>
          <div className="addpair-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="addpair-success-icon"><i className="fas fa-check-circle" /></div>
            <h3>Email Sent Successfully</h3>
            <p>Email has been sent:</p>
            <div className="addpair-success-details">
              <div className="addpair-success-row"><span>To</span><strong>{successModal.to}</strong></div>
              <div className="addpair-success-row"><span>Subject</span><strong>{successModal.subject}</strong></div>
              <div className="addpair-success-row">
                <span>Type</span><strong>{successModal.isRegistered ? "Registered user" : "External (non-registered)"}</strong>
              </div>
            </div>
            <button type="button" className="cp-btn cp-btn--primary" onClick={() => setSuccessModal(null)}>Done</button>
          </div>
        </div>
      )}
    </main>
  );
};

export default SendEmailPage;
